import { Box, Collapse, styled, useTheme } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { FC, Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import HeaderBgImage from '../../../../assets/images/header-bg.png'
import {
    FIELD_TYPES_SELECTION_RULE,
    FIELD_TYPE_COMPONENTS_TYPES,
    checkIfAnyFieldTypeMissingValue,
    updateFieldTypeJson,
} from 'packages/core'
import { useResourceTypeContext } from 'resourceTypeContext'
import { useApplicationFieldTypeSourceValues } from 'hooks'
import SingleSelectLookupControl from 'components/ApplicationFieldTypes/FieldTypeValuesControl/SingleSelectLookupControl'
import MultiSelectLookupControl from 'components/ApplicationFieldTypes/FieldTypeValuesControl/MultiSelectLookupControl'
import config from 'config'
import { FieldTypeDynamicComponents } from 'packages/eid-ui'
import ExpandableList from 'packages/eid-ui/ExpandableList'

export type AppRoleRightProps = {
    rightInfo?: any
    isAppRole: boolean
    isFormValidated: boolean
    defaultExpanded?: boolean
    showRequiredFieldMessage?: boolean
    isGroup?: boolean
    groupIndex?: number
}

const PaddedDiv = styled(Box)({
    margin: '0 20px',
})

const GradientSpacer = styled(Box)({
    background:
        'linear-gradient(180deg, #e9e9eb 0%, #f7f7f9 50%, #f1f1f3 100%)',
    height: '1.5rem',
})
const AppRoleRight: FC<AppRoleRightProps> = ({
    rightInfo,
    isAppRole,
    isFormValidated,
    defaultExpanded = false,
    showRequiredFieldMessage = false,
    isGroup = false,
    groupIndex = 0,
}) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const [showExpanded, setShowExpanded] = useState<boolean>(defaultExpanded)
    const showExpand =
        rightInfo?.FieldType?.length ||
        (isGroup && rightInfo?.groupedRights?.length > 0) ||
        false
    const [{ appRightsFieldTypes }, dispatch]: any = useResourceTypeContext()

    useEffect(() => {
        setShowExpanded(defaultExpanded)
    }, [defaultExpanded])

    useEffect(() => {
        if (isFormValidated && !showExpanded) {
            setShowExpanded(!checkIfAnyFieldTypeMissingValue(rightInfo))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFormValidated])

    const bindUIElement = (item: any) => {
        const valueType =
            item?.AzFieldTypeSelectionRuleTypeID ===
                FIELD_TYPES_SELECTION_RULE.MULTISELECT ||
            item?.AzFieldTypeSelectionRuleTypeID ===
                FIELD_TYPES_SELECTION_RULE.RELATIVE
                ? 'AssignedValues'
                : 'AssignedValue'
        const isApiSupportedSource = Boolean(
            item?.DataType &&
                item?.Name &&
                item?.FieldMethodName &&
                item?.FieldParameters,
        )

        if (
            item?.SelectionRule ===
            FIELD_TYPE_COMPONENTS_TYPES.MULTI_SELECT_LOOKUP_CONTROL
        ) {
            return (
                <MultiSelectLookupControl
                    fieldType={item}
                    isFormValidated={isFormValidated}
                    valueType={valueType}
                    isApiSupportedSource={isApiSupportedSource}
                    useDataHook={useApplicationFieldTypeSourceValues}
                    handleUpdate={(itemToUpdate: any) => {
                        return [
                            ...(rightInfo?.groupedRights ?? []),
                            rightInfo,
                        ]?.map((r) => {
                            dispatch({
                                type: 'UPDATE_APP_RIGHTS_FIELD_TYPES',
                                payload: updateFieldTypeJson(
                                    appRightsFieldTypes,
                                    'AzFieldTypeID',
                                    item.AzFieldTypeID,
                                    itemToUpdate,
                                    r?.AzLocalRightID,
                                ),
                            })
                        })
                    }}
                />
            )
        } else if (
            item?.SelectionRule ===
            FIELD_TYPE_COMPONENTS_TYPES.SINGLE_SELECT_LOOKUP_CONTROL
        ) {
            return (
                <SingleSelectLookupControl
                    fieldType={item}
                    isFormValidated={isFormValidated}
                    valueType={valueType}
                    isApiSupportedSource={isApiSupportedSource}
                    useDataHook={useApplicationFieldTypeSourceValues}
                    handleUpdate={(itemToUpdate: any) => {
                        return [
                            ...(rightInfo?.groupedRights ?? []),
                            rightInfo,
                        ]?.map((r) => {
                            dispatch({
                                type: 'UPDATE_APP_RIGHTS_FIELD_TYPES',
                                payload: updateFieldTypeJson(
                                    appRightsFieldTypes,
                                    'AzFieldTypeID',
                                    item.AzFieldTypeID,
                                    itemToUpdate,
                                    r?.AzLocalRightID,
                                ),
                            })
                        })
                    }}
                />
            )
        } else {
            const DynamicComponentToRender: any =
                FieldTypeDynamicComponents[item?.SelectionRule]
            if (!DynamicComponentToRender) {
                return null
            }
            return (
                <DynamicComponentToRender
                    fieldType={item}
                    isFormValidated={isFormValidated}
                    valueType={valueType}
                    AzLocalRightID={rightInfo?.AzLocalRightID}
                    isApiSupportedSource={isApiSupportedSource}
                    useDataHook={useApplicationFieldTypeSourceValues}
                    handleUpdate={(itemToUpdate: any) => {
                        return [
                            ...(rightInfo?.groupedRights ?? []),
                            rightInfo,
                        ]?.map((r) => {
                            dispatch({
                                type: 'UPDATE_APP_RIGHTS_FIELD_TYPES',
                                payload: updateFieldTypeJson(
                                    appRightsFieldTypes,
                                    'AzFieldTypeID',
                                    item.AzFieldTypeID,
                                    itemToUpdate,
                                    r?.AzLocalRightID,
                                ),
                            })
                        })
                    }}
                />
            )
        }
    }

    return (
        <Box
            sx={{
                boxShadow: '0 4px 22px 0 rgba(4, 171, 147, 0.13)',
                borderRadius: '4px',
                marginBottom: '24px',
                ...(showExpanded && {
                    boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.05)',
                    borderRadius: '8px',
                }),
                ...(showRequiredFieldMessage &&
                    !showExpanded && {
                        boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.08)',
                        border: 'solid 1px rgba(208, 2, 27, 0.25)',
                        backgroundColor: '#ffeff1',
                    }),
                ...(!showRequiredFieldMessage &&
                    !showExpanded && {
                        '&:not(:empty)': {
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                width: '95%',
                                height: '1px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                bottom: '-1px',
                                display: 'block',
                                opacity: '0.25',
                                position: 'absolute',
                                borderStyle: 'solid',
                                borderWidth: '1px',
                                borderImageSlice: '1',
                                borderImageSource:
                                    'linear-gradient(to right, rgba(1, 174, 143, 0), #04ab93 7%, #2c83bd 91%, rgba(48, 127, 193, 0))',
                            },
                        },
                    }),
            }}
        >
            {(rightInfo?.DisplayName || isGroup) && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '19px 16px',
                        background: 'transparent',
                        '& img': {
                            display: 'none',
                        },
                        ...(showExpanded && {
                            backgroundImage:
                                'linear-gradient(94deg, #682a94, #05afe0)',
                            border: 'none !important',
                            borderTopRightRadius: '8px',
                            borderTopLeftRadius: '8px',
                            position: 'relative',
                            overflow: 'hidden',
                            '& div': {
                                color: `${theme.palette.common.white} !important`,
                                borderColor: `${theme.palette.common.white} !important`,
                            },
                            '& span': {
                                color: theme.palette.common.white,
                            },
                            '& img': {
                                display: 'block',
                            },
                        }),
                    }}
                >
                    {showExpanded && (
                        <Box
                            sx={{
                                position: 'absolute',
                                height: '100%',
                                width: '100%',
                                zIndex: 0,
                                right: 0,
                                top: 0,
                            }}
                        >
                            <img
                                src={`${config.APP_SUBPATH}${HeaderBgImage}`}
                                alt="background"
                            />
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                fontSize: '1.8rem',
                                color: '#1b1f1f',
                                fontWeight: 600,

                                '& label': {
                                    display: 'inline-block',
                                    marginRight: '0.8rem',
                                    fontWeight: 300,
                                },
                            }}
                        >
                            {isAppRole && (
                                <label
                                    style={{
                                        color: !showExpanded
                                            ? '#8b909a'
                                            : '#fff',
                                    }}
                                >{`${
                                    isGroup
                                        ? t('GroupedAppRights')
                                        : t('Common_AppRight')
                                }:  `}</label>
                            )}
                            {`${
                                isGroup
                                    ? `${t('CommonGroup')} ${groupIndex}`
                                    : rightInfo?.DisplayName || '-'
                            }`}
                        </Box>
                        {showRequiredFieldMessage && (
                            <Box
                                style={{
                                    marginLeft: '1.9rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.7rem',
                                    color: showExpanded ? '#fff' : '#d0021b',
                                }}
                            >
                                <Icon
                                    style={{
                                        color: showExpanded
                                            ? '#fff'
                                            : '#d0021b',
                                        height: '1.4rem',
                                        width: '1.4rem',
                                    }}
                                    name={'CriticalRisk'}
                                />
                                {t('CompleteMandatoryFields')}
                            </Box>
                        )}
                        <Box
                            sx={{
                                padding: '0px 8px',
                                marginLeft: '12px',
                                color: '#5d6870',
                                fontSize: '10px',
                                marginTop: '5px',
                            }}
                        >
                            {rightInfo?.FieldType?.length
                                ? ''
                                : t('Common_NoFieldTypeValueFound')}
                        </Box>
                    </Box>
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                        fontSize="12px"
                        fontWeight="bold"
                        color={theme?.palette?.primary?.main}
                    >
                        {showExpand && (
                            <Box
                                component="span"
                                display="flex"
                                sx={{
                                    textTransform: 'uppercase',
                                    zIndex: 9999,
                                    marginTop: '0.2rem',
                                    '&:hover': {
                                        cursor: 'pointer',
                                    },
                                }}
                                onClick={() => setShowExpanded(!showExpanded)}
                            >
                                {showExpanded
                                    ? t('HideFieldTypes')
                                    : t('ShowFieldTypes')}
                                <Box
                                    sx={{
                                        width: '1.6rem',
                                        height: '1.6rem',
                                        marginLeft: '0.8rem',
                                        marginTop: '-0.1rem',
                                        color: theme?.palette?.primary?.main,
                                    }}
                                >
                                    {showExpanded ? (
                                        <Icon
                                            name="MinusCircle"
                                            width="1.6rem"
                                            height="1.6rem"
                                        />
                                    ) : (
                                        <Icon
                                            name="PlusCircle"
                                            width="1.6rem"
                                            height="1.6rem"
                                        />
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
            <Collapse
                style={{
                    borderRadius: '8px',
                    boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.05)',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderImageSource:
                        'linear-gradient(to right, #682a94 0%, #05afe0 100%)',
                    borderImageSlice: '1',
                    // backgroundImage:
                    //     'linear-gradient(to bottom, #fcfcfd, #fcfcfd), linear-gradient(to right, #682a94 0%, #05afe0 100%)',
                }}
                in={showExpanded}
                timeout={400}
                unmountOnExit
            >
                {isGroup && (
                    <>
                        <Box
                            sx={{
                                py: '2.8rem',
                                px: '2rem',
                            }}
                        >
                            <ExpandableList
                                visibleCount={3}
                                items={[
                                    rightInfo,
                                    ...rightInfo?.groupedRights,
                                ]?.map((r: any) => {
                                    return {
                                        friendlyName: r.DisplayName,
                                        id: r.AzLocalRightID,
                                        iconName: 'LocalRightsSmall',
                                        ...r,
                                    }
                                })}
                                headingLabel={'GroupedAppRights'}
                                headingHelperText="GroupedAppRightsHelperText"
                            />
                        </Box>
                        <GradientSpacer />
                    </>
                )}
                <PaddedDiv
                    sx={{
                        background: '#fbfbfd',
                        margin: '0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                    }}
                >
                    {rightInfo?.FieldType?.length
                        ? rightInfo?.FieldType.map(
                              (item: any, index: number) => (
                                  <Fragment key={`right-fieldtype-${index}`}>
                                      {bindUIElement(item)}
                                  </Fragment>
                              ),
                          )
                        : null}
                </PaddedDiv>
            </Collapse>
        </Box>
    )
}

export default AppRoleRight
